import { getEnvVariable } from '@/utils/get-env-variable';

const SCRIPT_SRC = 'https://sdk.checkouttools.com/v1.2/managedOrders.js';
const SCRIPT_ID = 'checkoutTools__script';
const FORTER_ID_TOKEN = getEnvVariable('VITE_FORTER_ID_TOKEN', '');

declare global {
  interface Window {
    checkoutTools?: {
      managedOrders: {
        manageOrder: (
          token: string,
          options: { challengeContainer?: () => HTMLElement | null },
          callback: (error: unknown) => void
        ) => void;
      };
    };
  }
}

export type ManageOrderOptions = {
  challengeContainer?: () => HTMLElement | null;
};

class Forter3DSService {
  private loadPromise: Promise<void> | null = null;
  private scriptElement: HTMLScriptElement | null = null;
  // Dedupe in-flight challenges per token. Initializing the same managed order
  // twice spins up a second ACS challenge session that invalidates the first,
  // so the shopper's (stale) challenge dead-ends with a `continueBrowserChallenge`
  // 404 / "Challenge not found". Returning the existing promise prevents that.
  private activeToken: string | null = null;
  private activePromise: Promise<void> | null = null;

  manageOrder(token: string, options: ManageOrderOptions = {}): Promise<void> {
    if (!FORTER_ID_TOKEN) {
      return Promise.reject(new Error('Forter3DS: VITE_FORTER_ID_TOKEN not configured'));
    }
    if (!token) {
      return Promise.reject(new Error('Forter3DS: managedOrderToken is required'));
    }

    // Set synchronously (before any await) so concurrent callers dedupe.
    if (this.activeToken === token && this.activePromise) {
      return this.activePromise;
    }

    this.activeToken = token;
    this.activePromise = this.runManageOrder(token, options).finally(() => {
      this.activeToken = null;
      this.activePromise = null;
    });
    return this.activePromise;
  }

  private async runManageOrder(token: string, options: ManageOrderOptions): Promise<void> {
    await this.ensureLoaded();

    if (!window.checkoutTools?.managedOrders?.manageOrder) {
      throw new Error('Forter3DS: managedOrders SDK is unavailable after load');
    }

    return new Promise((resolve, reject) => {
      window.checkoutTools!.managedOrders.manageOrder(
        token,
        { challengeContainer: options.challengeContainer ?? (() => null) },
        (error: unknown) => (error ? reject(error) : resolve())
      );
    });
  } // eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6IjIwMjMtMDUtMjMifQ.eyJtYW5hZ2VkT3JkZXJJZCI6ImVlODM3MjY4LThhOTgtNDI4Mi1iMTU1LTc2NDZkN2UxZTE2MiIsIm1ldGhvZFVSTCI6Imh0dHBzOi8vYWNzMTU5LnNhbmRib3guM2RzZWN1cmUuaW8vM2RzbWV0aG9kIiwidGhyZWVEU1NlcnZlclRyYW5zSUQiOiI4MDQzYWI1Ny01OGIyLTRkYTgtOWIyZS0wOTJiZjU1YWQwMDAiLCJ2ZXJzaW9uIjoiMi4yLjAiLCJhdWQiOiJjaGVja291dHRvb2xzIiwiaXNzIjoiaHR0cHM6Ly9hcGkuY2hlY2tvdXR0b29scy5jb20vdjMvbWFuYWdlZC9vcmRlcnMiLCJleHAiOjE3ODE0NTUwOTB9.LLmtq3QUgUMc73Ncj-iV3gt92DK5Us-FxojVxqxZdjN9YwTShYN04FaivrHTsMjqMtxhZyNAr5nx_A0lhskzhLjOHbIKtjYHPHTY6Q0augc6pEA0lmlseqLWfkSa_xw3f7jH-5jc4sgSwynbT5Im0bkpynHE7BvR8L1rNg0X4dhkHdWU_Qiwso86_03pCJtV1n2MsPX7MAArYMwxdDnPl47617QbtyKVizU_2FTmMApi4Iz35jVYhVi5dm4zmXj2A4O7UPhd79l87BPgzR_phB5tiNb9HyQP310tW68gPVyRuoni9rUt_xaAPFi4WwIo0VhPm_uL-G5IsrX4BUW8d2aRNLSDI0K7AtWYqr2WTZv8M86Sx5q_MValBTIL09F2ZKfR7EXOyfoZs7cQkceK7gjI5sI8gpuL5c5uhP-CqTpiV2P6AsCMBdEGuSSjWduzvJEZF02vio3GPRyyjP-SUlsC42QOthL_AW69DMXe8r4Ar9OGPgwavE73Ydg-R4T5p00atmFHCe9glHS1W42VDTdWiUSJLWut82QXoDBVvxnSI6q69RyAUkqPCA17STjP_MFfziUOv4Fn-PJF89UJTuD7J3KX7tOKT82mMToECH_8ZDIO3lu21wl9D6GT2IfWoBDiovQRKl7USCsJabRi3w6VwXCtOMTHMrkzSFzj1cQ

  isReady(): boolean {
    return Boolean(window.checkoutTools?.managedOrders?.manageOrder);
  }

  cleanup(): void {
    if (this.scriptElement && this.scriptElement.parentNode) {
      this.scriptElement.parentNode.removeChild(this.scriptElement);
      this.scriptElement = null;
    }
    this.loadPromise = null;
  }

  private ensureLoaded(): Promise<void> {
    if (typeof document === 'undefined') {
      return Promise.reject(new Error('Forter3DS: document is not available'));
    }
    if (window.checkoutTools) {
      return Promise.resolve();
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
      if (existing) {
        this.scriptElement = existing;
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Forter3DS: existing script failed to load')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'text/javascript';
      script.src = SCRIPT_SRC;
      script.setAttribute('data-site-id', FORTER_ID_TOKEN);
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Forter3DS: failed to load managedOrders script'));

      document.head.appendChild(script);
      this.scriptElement = script;
    }).catch((error) => {
      this.loadPromise = null;
      throw error;
    });

    return this.loadPromise;
  }
}

export const forter3DSService = new Forter3DSService();
